import React, { useState, useEffect, useContext, useMemo } from 'react';
import { FiPlus, FiEye, FiDollarSign, FiX, FiMessageSquare, FiClock, FiSearch, FiFilter, FiMessageCircle } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import AddClientModal from '../components/AddClientModal';
import AuthContext from '../context/AuthContext';
import FeatureComingSoonModal from '../components/FeatureComingSoonModal';
import Spinner from '../components/Spinner';

const DebtPaymentModal = ({ isOpen, onClose, onSave, debt }) => {
  const [amount, setAmount] = useState('');

  if (!isOpen) return null;

  const handleSave = () => {
    const paymentAmount = parseFloat(amount);
    if (!isNaN(paymentAmount) && paymentAmount > 0) {
      onSave(paymentAmount);
      setAmount('');
    } else {
      toast.error("Veuillez entrer un montant valide.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-sm">
        <h3 className="text-xl font-semibold mb-4">Enregistrer un paiement pour {debt?.clientId?.name || 'client'}</h3>
        <p className="mb-2">Montant dû: {(debt?.amountRemaining || 0).toLocaleString('fr-FR')} CFA</p>
        <input type="number" placeholder="Montant payé" value={amount} onChange={(e) => setAmount(e.target.value)} className="w-full p-2 border rounded mb-4" />
        <div className="flex justify-end space-x-3">
          <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg">Annuler</button>
          <button onClick={handleSave} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg">Enregistrer</button>
        </div>
      </div>
    </div>
  );
};

const DebtHistoryModal = ({ isOpen, onClose, debtId }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && debtId) {
      const fetchHistory = async () => {
        setLoading(true);
        try {
          const response = await api.get(`/debts/${debtId}/history`);
          setHistory(response.data);
        } catch (error) {
          toast.error("Impossible de charger l'historique.");
        }
        setLoading(false);
      };
      fetchHistory();
    }
  }, [isOpen, debtId]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Historique des paiements</h3>
          <button onClick={onClose}><FiX size={24} /></button>
        </div>
        {loading ? <p>Chargement...</p> : (
          <ul className="space-y-2">
            {history.length > 0 ? history.map(payment => (
              <li key={payment._id} className="flex justify-between p-2 bg-gray-100 rounded">
                <span>{new Date(payment.date).toLocaleDateString('fr-FR')}</span>
                <span className="font-bold text-green-600">{payment.amount.toLocaleString('fr-FR')} CFA</span>
              </li>
            )) : <p>Aucun paiement enregistré pour cette dette.</p>}
          </ul>
        )}
      </div>
    </div>
  );
};

const getStatusClass = (status) => {
  switch (status) {
    case 'Payé': return 'bg-green-200 text-green-900';
    case 'Partiellement payé': return 'bg-yellow-200 text-yellow-900';
    case 'Non payé': return 'bg-red-200 text-red-900';
    default: return 'bg-gray-200 text-gray-900';
  }
};

const Debts = () => {
  const [debts, setDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending'); // 'pending', 'Non payé', 'Partiellement payé', 'Payé', 'all'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('dueDate_asc'); // 'dueDate_asc', 'dueDate_desc', 'amount_desc', 'amount_asc'
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isAddClientModalOpen, setIsAddClientModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState(null);
  const [isSmsComingSoonModalOpen, setIsSmsComingSoonModalOpen] = useState(false);
  const [searchParams] = useSearchParams();
  const { user, openUpgradeModal } = useContext(AuthContext);

  const fetchDebts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/debts');
      let fetchedDebts = response.data;
      const clientId = searchParams.get('clientId');
      if (clientId) {
        fetchedDebts = fetchedDebts.filter(debt => debt.clientId?._id === clientId);
      }
      setDebts(fetchedDebts);
    } catch (error) {
      toast.error("La gestion des dettes clients est une fonctionnalité Pro. Passez au plan supérieur pour encaisser votre argent plus rapidement !", { icon: "💰" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDebts();
  }, [searchParams]); // Déjà correct ici, bon exemple !

  const filteredDebts = useMemo(() => {
    let result = debts;

    // Filtrage par statut
    if (statusFilter === 'pending') {
      result = result.filter(debt => debt.status !== 'Payé');
    } else if (statusFilter !== 'all') {
      result = result.filter(debt => debt.status === statusFilter);
    }

    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      result = result.filter(debt => 
        debt.clientId?.name?.toLowerCase().includes(lowerSearch)
      );
    }

    result.sort((a, b) => {
      switch (sortBy) {
        case 'dueDate_asc':
          return new Date(a.dueDate) - new Date(b.dueDate);
        case 'dueDate_desc':
          return new Date(b.dueDate) - new Date(a.dueDate);
        case 'amount_desc':
          return b.amountRemaining - a.amountRemaining;
        case 'amount_asc':
          return a.amountRemaining - b.amountRemaining;
        default:
          return 0;
      }
    });

    return result;
  }, [debts, statusFilter, searchTerm, sortBy]);

  const handleOpenPaymentModal = (debt) => {
    setSelectedDebt(debt);
    setIsPaymentModalOpen(true);
  };

  const handleOpenHistoryModal = (debt) => {
    setSelectedDebt(debt);
    setIsHistoryModalOpen(true);
  };

  const handleSavePayment = async (paymentAmount) => {
    try {
      await api.post(`/debts/${selectedDebt._id}/payments`, { amount: paymentAmount });
      toast.success("Paiement enregistré avec succès !");
      fetchDebts(); // Recharger la liste des dettes
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement du paiement.");
    }
    setIsPaymentModalOpen(false);
  };

  const handleSaveClient = async (clientData) => {
    try {
      await api.post('/clients', clientData);
      toast.success(`Client "${clientData.name}" ajouté avec succès !`);
      // Idéalement, il faudrait aussi rafraîchir la liste des clients si elle est affichée ailleurs.
    } catch (error) {
      toast.error("Erreur lors de l'ajout du client.");
    }
    setIsAddClientModalOpen(false);
  };

  const handleSendSmsReminder = async (debt) => {
    if (user?.plan === 'pro' || user?.plan === 'premium') {
      // La fonctionnalité SMS est en dev, on affiche le modal "Bientôt disponible" pour les utilisateurs autorisés.
      setIsSmsComingSoonModalOpen(true);
    } else {
      // L'utilisateur n'a pas le bon plan, on lui propose de mettre à niveau.
      toast.info("Envoyez des rappels SMS automatiques et professionnels. Cette fonctionnalité est disponible dans le plan Pro.", { icon: "💬" });
      openUpgradeModal();
    }
  };

  const handleWhatsAppReminder = (debt) => {
    if (!debt.clientId?.phone) {
      toast.error("Ce client n'a pas de numéro de téléphone.");
      return;
    }
    const shopName = user?.shopName || 'Notre Boutique';
    const amount = debt.amountRemaining.toLocaleString('fr-FR');
    const clientName = debt.clientId.name;
    
    const message = `Bonjour ${clientName},%0A%0ACeci est un rappel de ${shopName}.%0AVous avez une dette en cours de *${amount} CFA*.%0A%0AMerci de passer régler dès que possible. 🙏`;
    const phoneNumber = debt.clientId.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
  };

  if (loading) {
    return <Spinner message="Chargement des dettes..." />;
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between sm:items-center mb-6 gap-4">
        <h2 className="text-2xl font-semibold text-gray-800">Dettes Clients</h2>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setIsAddClientModalOpen(true)} className="bg-brand-green hover:bg-green-600 text-white font-bold py-2 px-4 rounded-lg flex items-center transition-colors duration-200">
            <FiPlus className="mr-2" />
            Ajouter un client
          </button>
        </div>
      </div>

      {/* Filtres et Recherche */}
      <div className="mb-6 bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4 items-center">
        <div className="relative flex-grow w-full">
          <FiSearch className="absolute top-1/2 left-3 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Rechercher un client..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-100 border-2 border-gray-200 rounded-lg py-2 pl-10 pr-4 focus:outline-none focus:border-brand-blue"
          />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-gray-100 border-2 border-gray-200 rounded-lg py-2 px-4 focus:outline-none focus:border-brand-blue w-full md:w-auto">
          <option value="pending">Dettes en cours</option>
          <option value="Non payé">Non payé</option>
          <option value="Partiellement payé">Partiellement payé</option>
          <option value="Payé">Payé</option>
          <option value="all">Tout afficher</option>
        </select>
        <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-gray-100 border-2 border-gray-200 rounded-lg py-2 px-4 focus:outline-none focus:border-brand-blue w-full md:w-auto">
          <option value="dueDate_asc">Échéance (plus proche)</option>
          <option value="dueDate_desc">Échéance (plus lointaine)</option>
          <option value="amount_desc">Montant (plus élevé)</option>
          <option value="amount_asc">Montant (plus faible)</option>
        </select>
      </div>

      {/* Vue Mobile (Cartes) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:hidden">
        {filteredDebts.map((debt) => (
          <div key={debt._id} className={`bg-white p-5 rounded-2xl shadow-sm border border-gray-100 border-l-4 hover:shadow-md transition-all duration-200 ${new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' ? 'border-l-red-500' : 'border-l-gray-200'}`}>
            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-bold text-gray-800">{debt.clientId?.name || 'Client supprimé'}</h3>
                <p className="text-xs text-gray-500">Échéance: {debt.dueDate ? new Date(debt.dueDate).toLocaleDateString('fr-FR') : '-'}</p>
              </div>
              <span className={`text-xs font-bold px-2 py-1 rounded-full ${getStatusClass(debt.status)}`}>
                {debt.status}
              </span>
            </div>
            <div className="mb-3">
              <p className="text-sm text-gray-600">Montant restant</p>
              <p className="text-xl font-bold text-red-600">{debt.amountRemaining.toLocaleString('fr-FR')} CFA</p>
            </div>
            <div className="flex justify-end space-x-3 border-t pt-3">
              <button onClick={() => handleOpenHistoryModal(debt)} className="text-gray-500 hover:text-brand-blue p-2 bg-gray-100 rounded-full" title="Voir l'historique">
                <FiEye className="w-4 h-4" />
              </button>
              <button onClick={() => handleOpenPaymentModal(debt)} className="text-gray-500 hover:text-brand-green p-2 bg-gray-100 rounded-full" title="Enregistrer un paiement">
                <FiDollarSign className="w-4 h-4" />
              </button>
              <button onClick={() => handleSendSmsReminder(debt)} className="text-gray-500 hover:text-blue-500 p-2 bg-gray-100 rounded-full" title="Envoyer un rappel SMS">
                <FiMessageSquare className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white shadow-sm rounded-2xl border border-gray-100 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full leading-normal">
            <thead className="sticky top-0 z-10">
              <tr className="border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-5 py-3">Client</th>
                <th className="px-5 py-3">Montant Dû</th>
                <th className="px-5 py-3">Échéance</th>
                <th className="px-5 py-3">Dernier Paiement</th>
                <th className="px-5 py-3">Statut</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDebts.length > 0 ? filteredDebts.map((debt) => (
                <tr key={debt._id} className={`border-b border-gray-200 hover:bg-gray-50 ${new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' ? 'bg-red-50' : ''}`}>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap font-semibold">{debt.clientId?.name || 'Client supprimé'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-red-600 whitespace-no-wrap font-bold">{debt.amountRemaining.toLocaleString('fr-FR')} CFA</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center">
                      {new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' && <FiClock className="text-red-500 mr-2" title="En retard" />}
                      <p className={`whitespace-no-wrap ${new Date(debt.dueDate) < new Date() && debt.status !== 'Payé' ? 'text-red-700 font-semibold' : 'text-gray-900'}`}>
                        {debt.dueDate ? new Date(debt.dueDate).toLocaleDateString('fr-FR') : '-'}
                      </p>
                    </div>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <p className="text-gray-900 whitespace-no-wrap">{debt.lastPaymentDate ? new Date(debt.lastPaymentDate).toLocaleDateString('fr-FR') : '-'}</p>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <span className={`relative inline-block px-3 py-1 font-semibold leading-tight ${getStatusClass(debt.status)} rounded-full`}>
                      {debt.status}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-sm">
                    <div className="flex items-center space-x-3">
                      <button onClick={(e) => { e.stopPropagation(); handleOpenHistoryModal(debt); }} className="text-gray-500 hover:text-brand-blue" title="Voir l'historique"><FiEye className="w-5 h-5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleOpenPaymentModal(debt); }} className="text-gray-500 hover:text-brand-green" title="Enregistrer un paiement"><FiDollarSign className="w-5 h-5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleSendSmsReminder(debt); }} className="text-gray-500 hover:text-blue-500" title="Envoyer un rappel SMS (Plan Pro)"><FiMessageSquare className="w-5 h-5" /></button>
                      <button onClick={(e) => { e.stopPropagation(); handleWhatsAppReminder(debt); }} className="text-gray-500 hover:text-green-500" title="Relance WhatsApp"><FiMessageCircle className="w-5 h-5" /></button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="text-center py-10 text-gray-500">Aucune dette trouvée pour ce filtre.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <DebtPaymentModal isOpen={isPaymentModalOpen} onClose={() => setIsPaymentModalOpen(false)} onSave={handleSavePayment} debt={selectedDebt} />
      <DebtHistoryModal isOpen={isHistoryModalOpen} onClose={() => setIsHistoryModalOpen(false)} debtId={selectedDebt?._id} />
      <AddClientModal isOpen={isAddClientModalOpen} onClose={() => setIsAddClientModalOpen(false)} onSave={handleSaveClient} />
      <FeatureComingSoonModal
        isOpen={isSmsComingSoonModalOpen}
        onClose={() => setIsSmsComingSoonModalOpen(false)}
        message={`L'envoi de rappels SMS est en cours de développement et sera bientôt disponible. Merci de votre patience !`}
      />
    </div>
  );
};

export default Debts;